// Личные «Образы» покупателя (fd-discovery-04): свои подборки товаров.
// Создать (можно пустой), переименовать, открыть по ссылке, добавить товар из
// карточки («В подборку»), убрать товар, удалить. Схемы, http и мок живут
// здесь, а не в общих types.ts/http.ts: в `Api` это `api.looks.*`.
//
// Шлюз: /buyer/collections (services/discovery.ts). Образ закрыт по
// умолчанию; открытый виден по ссылке /feed/<slug>, но в общую ленту идей не
// попадает.
import { z } from "zod"

export const Look = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string().default(""),
  visibility: z.enum(["public", "private"]),
  productIds: z.array(z.string()),
  productCount: z.number(),
  cover: z.string().default(""),
  updatedAt: z.string(),
})
export type Look = z.infer<typeof Look>

export type LookInput = { title: string; visibility?: "public" | "private"; productIds?: string[] }
export type LookPatch = { title?: string; visibility?: "public" | "private"; productIds?: string[] }

export type LooksApi = {
  list(): Promise<Look[]>
  get(id: string): Promise<Look>
  create(input: LookInput): Promise<Look>
  update(id: string, patch: LookPatch): Promise<Look>
  remove(id: string): Promise<void>
  addProduct(id: string, productId: string): Promise<Look>
  removeProduct(id: string, productId: string): Promise<Look>
}

type Requester = <T>(path: string, opts?: { method?: string; body?: unknown }) => Promise<T>

const enc = encodeURIComponent

export const createLooksHttp = (request: Requester): LooksApi => ({
  list: async () => Look.array().parse(await request("/buyer/collections")),
  get: async (id) => Look.parse(await request(`/buyer/collections/${enc(id)}`)),
  create: async (input) =>
    Look.parse(
      await request("/buyer/collections", { method: "POST", body: { productIds: [], ...input } }),
    ),
  update: async (id, patch) =>
    Look.parse(await request(`/buyer/collections/${enc(id)}`, { method: "PATCH", body: patch })),
  remove: async (id) => {
    await request(`/buyer/collections/${enc(id)}`, { method: "DELETE" })
  },
  addProduct: async (id, productId) =>
    Look.parse(
      await request(`/buyer/collections/${enc(id)}/products`, {
        method: "POST",
        body: { productId },
      }),
    ),
  removeProduct: async (id, productId) =>
    Look.parse(
      await request(`/buyer/collections/${enc(id)}/products/${enc(productId)}`, {
        method: "DELETE",
      }),
    ),
})

/** Мок: образы живут в памяти вкладки, обложка - фото первого товара. */
export const createLooksMock = (photoOf: (productId: string) => string | undefined): LooksApi => {
  const looks = new Map<string, Look>()
  let seq = 0
  const save = (look: Look): Look => {
    const next = {
      ...look,
      productCount: look.productIds.length,
      cover: look.productIds[0] ? (photoOf(look.productIds[0]) ?? "") : "",
      updatedAt: new Date().toISOString(),
    }
    looks.set(next.id, next)
    return next
  }
  const must = (id: string): Look => {
    const look = looks.get(id)
    if (!look) throw new Error("Collection not found")
    return look
  }
  return {
    list: async () => [...looks.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    get: async (id) => must(id),
    create: async (input) => {
      seq += 1
      const id = `dcol_mock_${seq}`
      return save({
        id,
        slug: `look-${id}`,
        title: input.title,
        description: "",
        visibility: input.visibility ?? "private",
        productIds: [...new Set(input.productIds ?? [])],
        productCount: 0,
        cover: "",
        updatedAt: "",
      })
    },
    update: async (id, patch) => save({ ...must(id), ...patch }),
    remove: async (id) => {
      looks.delete(id)
    },
    addProduct: async (id, productId) => {
      const look = must(id)
      return look.productIds.includes(productId)
        ? look
        : save({ ...look, productIds: [...look.productIds, productId] })
    },
    removeProduct: async (id, productId) => {
      const look = must(id)
      return save({ ...look, productIds: look.productIds.filter((p) => p !== productId) })
    },
  }
}
